
import { useState } from 'react'
import useSWR from 'swr'
import {
  BarChart3, TrendingUp, Zap, Clock, AlertCircle,
  Activity, RefreshCw, ChevronDown, Hash,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts'

const fetcher = (url: string) => fetch(url).then(r => r.json())

function formatNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

function formatMs(n: number): string {
  return `${Math.round(n)}ms`
}

const CHART_TOOLTIP_STYLE = {
  background: 'var(--card)',
  border: '1px solid var(--border)',
  borderRadius: '6px',
  fontSize: '11px',
  color: 'var(--foreground)',
}

interface OverviewMetrics {
  total_requests: number
  total_tokens: number
  avg_latency: number
  error_rate: number
}

interface DailyUsage {
  date: string
  requests: number
  tokens: number
}

interface ModelUsage {
  model: string
  count: number
}

interface KeyUsage {
  key_name: string
  fingerprint: string
  request_count: number
}

interface AnalyticsData {
  overview: OverviewMetrics
  dailyUsage: DailyUsage[]
  topModels: ModelUsage[]
  topKeys: KeyUsage[]
}

const RANGE_OPTIONS = [
  { label: '24 hours', value: '1d' },
  { label: '7 days', value: '7d' },
  { label: '30 days', value: '30d' },
  { label: '90 days', value: '90d' },
]

const BAR_COLORS = [
  'var(--lava)',
  'var(--cyan-accent)',
  'var(--success)',
  'var(--warning)',
  'var(--chart-5)',
]

export default function AnalyticsPage() {
  const [range, setRange] = useState('7d')
  const { data, isLoading, mutate } = useSWR<AnalyticsData>(
    `/api/analytics?range=${range}`,
    fetcher,
    { refreshInterval: 30_000 }
  )

  const overview = data?.overview
  const dailyUsage = data?.dailyUsage ?? []
  const topModels = data?.topModels ?? []
  const topKeys = data?.topKeys ?? []

  const maxModel = topModels.reduce((m, r) => Math.max(m, r.count), 1)

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-md bg-lava/10">
            <BarChart3 className="w-4 h-4 text-lava" />
          </div>
          <div>
            <h2 className="text-base font-bold text-foreground">Analytics</h2>
            <p className="text-xs text-muted-foreground">
              Live usage metrics from D1 · auto-refreshes every 30s
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="h-8 w-32 text-xs border-border bg-card">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_OPTIONS.map(o => (
                <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 border-border"
            onClick={() => mutate()}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          {
            label: 'Total Requests',
            value: overview ? formatNum(overview.total_requests) : '—',
            icon: Zap,
            accent: 'lava',
            sub: `in ${RANGE_OPTIONS.find(o => o.value === range)?.label}`,
          },
          {
            label: 'Total Tokens',
            value: overview ? formatNum(overview.total_tokens) : '—',
            icon: Activity,
            accent: 'cyan',
            sub: 'input + output combined',
          },
          {
            label: 'Avg Latency',
            value: overview ? formatMs(overview.avg_latency) : '—',
            icon: Clock,
            accent: 'success',
            sub: 'end-to-end gateway',
          },
          {
            label: 'Error Rate',
            value: overview?.error_rate != null ? `${overview.error_rate.toFixed(1)}%` : '—',
            icon: AlertCircle,
            accent: overview?.error_rate && overview.error_rate > 5 ? 'warning' : 'success',
            sub: '4xx + 5xx responses',
          },
        ].map((card) => {
          const Icon = card.icon
          const accentMap: Record<string, string> = {
            lava: 'bg-lava/10 text-lava',
            cyan: 'bg-cyan-accent/10 text-cyan-accent',
            success: 'bg-success/10 text-success',
            warning: 'bg-warning/10 text-warning',
          }
          return (
            <Card key={card.label} className="bg-card border-border hover:border-lava/20 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground font-medium">{card.label}</p>
                    <p className="text-2xl font-bold text-foreground mt-1 tabular-nums">
                      {isLoading ? (
                        <span className="inline-block h-7 w-16 bg-muted rounded animate-pulse" />
                      ) : card.value}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{card.sub}</p>
                  </div>
                  <div className={`p-2 rounded-md flex-shrink-0 ${accentMap[card.accent]}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Daily volume chart */}
        <Card className="lg:col-span-2 bg-card border-border">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-sm font-semibold">Request Volume</CardTitle>
              <p className="text-[11px] text-muted-foreground mt-0.5">Requests per day</p>
            </div>
            <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-lava" /> Requests
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-cyan-accent" /> Tokens ÷ 100
              </span>
            </div>
          </CardHeader>
          <CardContent className="p-0 pb-4">
            {dailyUsage.length === 0 ? (
              <div className="h-[200px] flex items-center justify-center">
                <p className="text-xs text-muted-foreground">No usage data for this period</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={dailyUsage} margin={{ top: 8, right: 16, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="reqGradA" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--lava)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="var(--lava)" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="tokGradA" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--cyan-accent)" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="var(--cyan-accent)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(d) => d.slice(5)}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={formatNum}
                  />
                  <Tooltip
                    contentStyle={CHART_TOOLTIP_STYLE}
                    formatter={(v: number, name: string) => [
                      name === 'tokens' ? formatNum(v * 100) : formatNum(v),
                      name === 'tokens' ? 'Tokens' : 'Requests',
                    ]}
                  />
                  <Area type="monotone" dataKey="requests" stroke="var(--lava)" strokeWidth={1.5} fill="url(#reqGradA)" dot={false} />
                  <Area
                    type="monotone"
                    dataKey="tokens"
                    stroke="var(--cyan-accent)"
                    strokeWidth={1.5}
                    fill="url(#tokGradA)"
                    dot={false}
                    // scale down tokens for chart readability
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Top models */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Top Models</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2.5 pt-0">
            {topModels.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">No model data yet</p>
            ) : (
              topModels.map((m, i) => (
                <div key={m.model} className="space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-mono text-foreground truncate max-w-[160px]" title={m.model}>
                      {m.model.replace('@cf/', '').replace('@hf/', '')}
                    </span>
                    <span className="text-[11px] text-muted-foreground tabular-nums flex-shrink-0">
                      {formatNum(m.count)}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${(m.count / maxModel) * 100}%`,
                        background: BAR_COLORS[i] ?? 'var(--lava)',
                      }}
                    />
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom row: key usage + latency distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top keys */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Hash className="w-4 h-4 text-lava" />
              Top API Keys
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {topKeys.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">No key activity yet</p>
            ) : (
              <div className="space-y-0.5">
                {topKeys.map((k, i) => (
                  <div
                    key={k.fingerprint}
                    className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-muted/50 transition-colors"
                  >
                    <span className="text-[10px] font-mono text-muted-foreground w-4 text-right">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-foreground truncate">{k.key_name}</p>
                      <code className="text-[9px] text-muted-foreground font-mono">{k.fingerprint}</code>
                    </div>
                    <Badge variant="outline" className="text-[10px] font-mono tabular-nums border-lava/20 text-lava bg-lava/5 flex-shrink-0">
                      {formatNum(k.request_count)}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Model bar chart */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Model Distribution</CardTitle>
          </CardHeader>
          <CardContent className="p-0 pb-4">
            {topModels.length === 0 ? (
              <div className="h-[180px] flex items-center justify-center">
                <p className="text-xs text-muted-foreground">No data available</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart
                  data={topModels.map(m => ({
                    ...m,
                    model: m.model.split('/').pop()?.split(':')[0] ?? m.model,
                  }))}
                  margin={{ top: 4, right: 16, left: -20, bottom: 24 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis
                    dataKey="model"
                    tick={{ fontSize: 9, fill: 'var(--muted-foreground)' }}
                    axisLine={false}
                    tickLine={false}
                    angle={-20}
                    textAnchor="end"
                  />
                  <YAxis
                    tick={{ fontSize: 9, fill: 'var(--muted-foreground)' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={formatNum}
                  />
                  <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(v: number) => [formatNum(v), 'Requests']} />
                  <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                    {topModels.map((_, i) => (
                      <Cell key={i} fill={BAR_COLORS[i] ?? 'var(--lava)'} opacity={0.85} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
